import {Box, Button, Divider, Stack, Typography} from '@mui/material';
import {CTAButtonFixed, Snackbar} from 'presentation/components';
import {Loading} from 'presentation/components/Loading/Loading';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import React, {Fragment} from 'react';
import {Controller, FormProvider} from 'react-hook-form';
import {useProviderSelection} from 'presentation/pages/private/authorizations/hooks/useProviderSelection';
import {ModalError} from 'presentation/components/ModalError';
import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import Icon from 'presentation/icons';
import {HandleMessage} from 'presentation/components/HandleMessage';
import {ProviderDrawer} from '../ProviderDrawer';
import {CustomCTAButtonFixedAction, ProviderSelectionContainer, StyledCard} from './styles';
import {ProviderSelectionProps} from './types';
import {ProviderPreferenceRadioGroup} from './components/ProviderPreferenceRadioGroup';
import {ProviderCard} from './components/ProviderCard';
import {ObservationTextField} from './components/ObservationTextField';

const {keep, tryAgain, close, notice, add} = translations['pt-br'].commons;
const {title, includeThreeProviders, removedProvider, infoOne, infoTwo} =
	translations['pt-br'].privateFlow.requestAuthorizations.request.ProviderSelection;
const {description: descriptionModal, title: titleModal} =
	translations['pt-br'].privateFlow.requestAuthorizations.errorModal;

export function ProviderSelection({onNextStep}: ProviderSelectionProps) {
	const {isMobile, isTablet} = useMediaQueries();

	const {
		methods,
		hasProviderPreference,
		observationState,
		errorObservationState,
		errorRadioState,
		isGetProvidersFetching,
		providersList,
		onOpenDrawer,
		openDrawerState,
		onCloseDrawer,
		warningMessageState,
		showSnackbarState,
		setShowSnackbarState,
		selectedState,
		setSelectedState,
		resetFieldsOnPreferenceChange,
		control,
		IS_POST_AUTH_REQUEST_ERROR,
		IS_POST_AUTH_REQUEST_LOADING,
		handleCloseModalError,
		handleSetObservationState,
		handleCreateNewRequest,
	} = useProviderSelection(onNextStep);

	const showObservation = hasProviderPreference === true;

	return (
		<Fragment>
			<ProviderSelectionContainer px={isMobile ? 2 : 4} py={isMobile ? 2 : 3}>
				<Stack gap={2} pb={isTablet ? 1 : 2} borderRadius={1}>
					<StyledCard>
						<Typography fontWeight={600} color="text.primary">
							{title}
						</Typography>
						<Stack direction="row" alignItems="center" gap={2}>
							<Icon
								style={{
									minWidth: 24,
									alignSelf: 'baseline',
								}}
								name="medicalinformation-default"
							/>
							<Typography variant="body1" color="text.primary">
								<HandleMessage message={infoOne} />
							</Typography>
						</Stack>
						<Stack direction="row" alignItems="center" gap={2}>
							<Icon
								style={{
									minWidth: 24,
									alignSelf: 'baseline',
								}}
								name="person-search"
							/>
							<Typography variant="body1" color="text.primary">
								{infoTwo}
							</Typography>
						</Stack>
						<FormProvider {...methods}>
							<Controller
								name="hasProviderPreference"
								control={control}
								defaultValue={undefined}
								render={({field: {value, onChange}}) => (
									<ProviderPreferenceRadioGroup
										value={value}
										onChange={val => {
											onChange(val);
											resetFieldsOnPreferenceChange();
										}}
										error={errorRadioState}
									/>
								)}
							/>
						</FormProvider>
					</StyledCard>

					{hasProviderPreference === true ? (
						<Fragment>
							<StyledCard>
								<Typography fontWeight={600} color="text.primary">
									{includeThreeProviders} {`(${providersList.length}/3)`}
								</Typography>

								<Box>
									<Button
										variant="outlined"
										color="primary"
										size="medium"
										onClick={onOpenDrawer}
										fullWidth
										data-tag-id={AutoSolEnum.CLICK_BUSCAR_PRESTADORES}
										endIcon={
											isGetProvidersFetching ? (
												<Box width={24}>
													<Loading color="inherit" size={14} />
												</Box>
											) : null
										}>
										{add}
									</Button>

									{warningMessageState ? (
										<Typography variant="caption" color="error" mt={1}>
											{warningMessageState}
										</Typography>
									) : null}
								</Box>

								{providersList
									? providersList.map(provider => (
											<ProviderCard
												removeButtonDataTagId={`${AutoSolEnum.CLICK_REMOVER_PRESTADOR_AUTOSOL}${provider.cdPrestador}`}
												key={provider?.idUnico}
												provider={provider}
												onDeleteProvider={() => setShowSnackbarState(true)}
											/>
										))
									: null}
							</StyledCard>
							<Divider />
						</Fragment>
					) : null}

					{showObservation ? (
						<ObservationTextField
							value={observationState}
							onChange={handleSetObservationState}
							error={!!errorObservationState}
						/>
					) : null}
				</Stack>

				<CTAButtonFixed.Root $isTablet={isTablet}>
					<CustomCTAButtonFixedAction
						fullWidth={isTablet}
						onClick={handleCreateNewRequest}
						data-tag-id={AutoSolEnum.CLICK_CONTINUAR_DETALHES_DA_SOLICITACAO}
						size="large"
						sx={{alignSelf: 'flex-end'}}
						disabled={IS_POST_AUTH_REQUEST_LOADING}
						endIcon={
							IS_POST_AUTH_REQUEST_LOADING ? (
								<Box width={24}>
									<Loading color="inherit" size={14} />
								</Box>
							) : null
						}>
						{keep}
					</CustomCTAButtonFixedAction>
				</CTAButtonFixed.Root>
			</ProviderSelectionContainer>

			<ProviderDrawer
				openDrawer={openDrawerState}
				onCloseDrawer={onCloseDrawer}
				onOpenDrawer={onOpenDrawer}
				selectedState={selectedState}
				setSelectedState={setSelectedState}
			/>

			{showSnackbarState ? (
				<Snackbar
					message={removedProvider}
					open={showSnackbarState}
					onClose={() => setShowSnackbarState(false)}
					sx={{mb: isTablet ? '120px' : undefined}}
				/>
			) : null}

			{IS_POST_AUTH_REQUEST_ERROR ? (
				<ModalError
					open={IS_POST_AUTH_REQUEST_ERROR}
					onClose={handleCloseModalError}
					headerTitle={notice}
					title={titleModal}
					description={descriptionModal}
					maxWidth={420}
					firstButton={{
						title: tryAgain,
						handleClick: handleCreateNewRequest,
					}}
					secondButton={{
						title: close,
						handleClick: () => handleCloseModalError(),
					}}
				/>
			) : null}
		</Fragment>
	);
}
